import React, { Component } from 'react'
import { provideState, injectState, softUpdate, hardUpdate } from 'freactal'
import app from '../../feathers'
import styled from 'styled-components'
import { Switch, Route, Link } from 'react-router-dom'
import { FABButton, Icon } from 'react-mdl'
import Button from '../../components/Button'
import Portal from 'react-portal'
import UnitSettings from './UnitSettings'
import GenerateDocuments from './GenerateDocuments'
import LearnerGuide from './LearnerGuide'
import PresentationContent from './PresentationContent'
import ELearningPresentationContent from './ELearningPresentationContent'
import SummativePractical from './SummativePractical'
import AssessmentPlan from './AssessmentPlan'
import FormativePracticalAssessments from './FormativePracticalAssessments'
import UnitNotesModal from '../components/UnitNotesModal'
import SectionTableModal from './SectionTableModal'

import { getSlides, hasLearnerGuideContent, hasAssessments } from '../../lib'

const units = app.service('units')

const Section = styled.div`
  padding: 1px;
`
const Content = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`
const Item = styled.div`
  width: 100%;
  flex: 1;
`
const ButtonsContainer = styled.div`
  & > * {
    display: inline-block;
    margin-top: 10px;
    margin-right: 10px;
    margin-bottom: 10px;
  }
`
const ActiveLight = styled.span`
  display: inline-block;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  background: ${props => (props.condition ? '#00CA31' : '#FF0000')};
  border: 2px solid black;
  vertical-align: : 'bottom';
  float: right;
`
const UnitNotesModalButton = ({ onClick }) => (
  <FABButton
    mini
    colored
    raised
    onClick={onClick}
    style={{
      position: 'absolute',
      top: '0.5em',
      right: '0.25em',
      zIndex: 100,
    }}>
    <Icon name="book" />
  </FABButton>
)

const withState = provideState({
  initialState: () => ({
    unit: null,
    unitLoading: false,
    unitLoadingError: null,
    unitNotesModalOpen: false,
    unitNotes: '',
  }),
  effects: {
    setUnitLoading: softUpdate((_, unitLoading) => ({ unitLoading })),
    setUnit: softUpdate((_, unit) => ({ unit, unitNotes: unit.notes })),
    fetchUnit: async (effects, unitId) => {
      try {
        await effects.setUnitLoading(true)
        const unit = await units.get(unitId)
        await effects.setUnitLoading(false)
        await effects.setUnit(unit)
      } catch (unitLoadingError) {
        return state => ({ ...state, unitLoadingError })
      }
    },
    handleUnitNotesChange: (effects, unitNotes = '') => {
      return state => {
        return {
          ...state,
          unitNotes,
        }
      }
    },
    handleUnitNotesModalOpen: hardUpdate({ unitNotesModalOpen: true }),
    handleUnitNotesModalClose: hardUpdate({ unitNotesModalOpen: false }),
    handleUnitNotesSave: () => async ({ unit, unitNotes }) => {
      try {
        if (unit.notes === unitNotes) return
        await units.patch(unit._id, {
          notes: unitNotes,
        })
      } catch (err) {
        // ignore notes failing to save errors
      }
    },
  },
})

class Unit extends Component {
  componentDidMount() {
    const { match: { params: { unitId } } } = this.props
    this.props.effects.fetchUnit(unitId)
    app.service('units').on('updated', this.handleUnitUpdate)
    app.service('units').on('patched', this.handleUnitUpdate)
  }
  componentWillUnmount() {
    app.service('units').off('updated', this.handleUnitUpdate)
    app.service('units').off('patched', this.handleUnitUpdate)
  }
  handleUnitUpdate = unit => {
    if (unit._id !== this.props.match.params.unitId) return
    this.props.effects.setUnit(unit)
  }
  render() {
    // eslint-disable-next-line no-console
    const {
      state: { unit, unitLoading, unitLoadingError, unitNotesModalOpen, profile },
      effects: { handleUnitNotesModalOpen, snackbarAlert },
    } = this.props
    if (unitLoadingError) {
      return <div>Error while loading: {unitLoadingError.message}</div>
    }
    if (unitLoading || !unit) {
      return <div>Loading unit...</div>
    }
    const hasPresentationSlides = getSlides(unit, 'presentation').length !== 0
    const hasELearningPresentationSlides =
      getSlides(unit, 'eLearningPresentation').length !== 0

    return (
      <div>
        <Switch>
          <Route
            strict
            path="/units/:unitId/settings/"
            component={UnitSettings}
          />
          <Route
            strict
            path="/units/:unitId/generate/"
            component={GenerateDocuments}
          />
          <Route
            strict
            path="/units/:unitId/learner-guide/"
            component={LearnerGuide}
          />
          <Route
            strict
            path="/units/:unitId/presentation/"
            component={PresentationContent}
          />
          <Route
            strict
            path="/units/:unitId/e-learning-presentation/"
            component={ELearningPresentationContent}
          />
          <Route
            strict
            path="/units/:unitId/summative-practicals/"
            component={SummativePractical}
          />
          <Route
            strict
            path="/units/:unitId/assessment-plan/"
            component={AssessmentPlan}
          />
          <Route
            strict
            path="/units/:unitId/formative-practical-assessments/"
            component={FormativePracticalAssessments}
          />
          <Route
            component={() => (
              <div>
                <h4>
                  {unit.code} - {unit.title}
                </h4>
                <Section header="Settings">
                  <Content>
                    <Item>
                      <p>
                        <b>Title:</b> {unit.title}
                      </p>
                      <p>
                        <b>Topic:</b> {unit.topic}
                      </p>
                      <p>
                        <b>Industry:</b> {unit.industry}
                      </p>
                      <ButtonsContainer>
                        <Link to="./settings/">
                          <Button raised primary>
                            Unit Settings
                          </Button>
                        </Link>
                      </ButtonsContainer>
                    </Item>
                    <Item>
                      <p>
                        <b>Has Assessments: </b>{' '}
                        <ActiveLight condition={hasAssessments(unit)} />
                      </p>
                      <p>
                        <b>Learner guide section content: </b>{' '}
                        <ActiveLight
                          condition={hasLearnerGuideContent(unit).length === 0}
                        />
                      </p>
                      <p>
                        <b>Presentation Slides </b>{' '}
                        <ActiveLight condition={hasPresentationSlides} />
                      </p>
                      <p>
                        <b>eLearning Slides </b>{' '}
                        <ActiveLight
                          condition={hasELearningPresentationSlides}
                        />
                      </p>
                      <ButtonsContainer>
                        <Link to="./incomplete-content/">
                          <Button raised primary>
                            See incomplete content
                          </Button>
                        </Link>
                      </ButtonsContainer>
                    </Item>
                  </Content>
                  <hr />
                  <Route
                    strict
                    path="/units/:unitId/incomplete-content/"
                    component={SectionTableModal}
                  />
                </Section>
                <Section>
                  <h4>Learner Guide and Theory Assessment</h4>
                  <ButtonsContainer>
                    <Link to="./learner-guide/">
                      <Button raised primary>
                        Learner Guide Content
                      </Button>
                    </Link>
                  </ButtonsContainer>
                  <hr />
                </Section>
                {profile && profile.features.powerpoint
                  ?
                  <Section>
                    <h4>Presentation</h4>
                    <ButtonsContainer>
                      <Link to="./presentation/settings/">
                        <Button raised primary>
                          PPTX Presentation Settings
                    </Button>
                      </Link>
                      <Link to="./presentation/">
                        <Button
                          raised
                          primary
                          disabled={!hasPresentationSlides}
                          onDisabledClick={e => {
                            e.preventDefault()
                            snackbarAlert('Please initialise slides')
                          }}>
                          PPTX Presentation Content
                    </Button>
                      </Link>
                      <Link to="./e-learning-presentation/settings/">
                        <Button raised primary>
                          eLearning Presentation Settings
                    </Button>
                      </Link>
                      <Link to="./e-learning-presentation/">
                        <Button
                          raised
                          primary
                          disabled={!hasELearningPresentationSlides}
                          onDisabledClick={e => {
                            e.preventDefault()
                            snackbarAlert('Please initialise slides')
                          }}>
                          eLearning Presentation Content
                    </Button>
                      </Link>
                    </ButtonsContainer>
                    <hr />
                  </Section>
                  : null}
                <Section>
                  <h4>Practical Assessment</h4>
                  <ButtonsContainer>
                    <Link to="./summative-practicals/">
                      <Button raised primary>
                        Summative Practical Assessments Content
                      </Button>
                    </Link>
                    <Link to="./assessment-plan/">
                      <Button raised primary>
                        Assessment Plan
                      </Button>
                    </Link>
                    <Link to="./formative-practical-assessments/">
                      <Button raised primary>
                        Formative Practical Assessments
                      </Button>
                    </Link>
                  </ButtonsContainer>
                  <hr />
                </Section>
                {profile && profile.features.generateReport
                  ?
                  <Section header="Generate Documents">
                    <h4>Generate Documents</h4>
                    <ButtonsContainer>
                      <Link to="./generate/">
                        <Button raised primary>
                          Generate Documents
                      </Button>
                      </Link>
                    </ButtonsContainer>
                    <hr />
                  </Section>
                  : null
                }
              </div>
            )}
          />
        </Switch>
        <Portal isOpened>
          <UnitNotesModalButton onClick={handleUnitNotesModalOpen} />
        </Portal>
        {unitNotesModalOpen && <UnitNotesModal />}
      </div>
    )
  }
}

export default withState(injectState(Unit))



// WEBPACK FOOTER //
// ./src/Units/ManageUnit/index.js